import { Component, OnDestroy, OnInit } from '@angular/core';
import { EventEmitter, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ResetPasswordService } from '../../../../core/services/reset-password.service';
import { TiposDocumentoService } from '../../../../core/services/tipos-documento.service';
import { UiHelperService } from '../../../../services/ui-helper.service';
import { NotificationService } from '../../../../services/notification.service';
import { calcularDigitoVerificacionNIT } from '../../../../core/utils/nit.util';
import {
  TipoDocumentoOption,
  abreviaturaPorIdTipo,
  esAbreviaturaDocumentoNit,
  maxLengthNumeroDocumento,
  mensajeNumeroDocumentoInvalido,
  numeroDocumentoInvalido,
  sanitizarNumeroDocumento,
  tipoDocumentoPermiteAlfanumerico
} from '../../../../core/utils/tipos-documento.util';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-reset-step-identificacion',
  templateUrl: './step-identificacion.component.html',
  styleUrls: ['./step-identificacion.component.css'],
  standalone: false
})
export class StepIdentificacionComponent implements OnInit, OnDestroy {
  @Output() next = new EventEmitter<void>();

  form!: FormGroup;
  tiposDocumentoOptions: TipoDocumentoOption[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private resetPassword: ResetPasswordService,
    private tiposDocumentoService: TiposDocumentoService,
    private uiHelper: UiHelperService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        tipo: ['0', [Validators.required, this.tipoRequeridoValidator()]],
        numero: ['', [Validators.required]]
      },
      { validators: this.numeroPorTipoValidator() }
    );

    this.tiposDocumentoService
      .opcionesSelectPorId$('todos')
      .pipe(takeUntil(this.destroy$))
      .subscribe((opts) => (this.tiposDocumentoOptions = opts));

    this.form
      .get('tipo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const numControl = this.form.get('numero');
        const limpio = sanitizarNumeroDocumento(this.abreviaturaActual, numControl?.value);
        if (limpio !== String(numControl?.value ?? '')) {
          numControl?.setValue(limpio, { emitEvent: true });
        }
        numControl?.updateValueAndValidity();
        this.form.updateValueAndValidity({ emitEvent: false });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  tipoRequeridoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = control.value;
      if (v === '0' || v === null || v === '') {
        return { tipoInvalido: true };
      }
      return null;
    };
  }

  numeroPorTipoValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const tipoId = group.get('tipo')?.value;
      const numero = String(group.get('numero')?.value ?? '').trim();
      const numControl = group.get('numero');
      if (!tipoId || tipoId === '0' || !numero) {
        return null;
      }
      const abrev = this.abreviaturaDeId(tipoId);
      if (numeroDocumentoInvalido(abrev, numero)) {
        numControl?.setErrors({ ...(numControl.errors || {}), sincodigo: true });
        return { sincodigo: true };
      }
      if (numControl?.hasError('sincodigo')) {
        const { sincodigo: _drop, ...rest } = numControl.errors || {};
        numControl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    };
  }

  get abreviaturaActual(): string {
    return this.abreviaturaDeId(this.form?.get('tipo')?.value);
  }

  get esNIT(): boolean {
    return esAbreviaturaDocumentoNit(this.abreviaturaActual);
  }

  get soloNumeros(): boolean {
    const a = this.abreviaturaActual;
    return !tipoDocumentoPermiteAlfanumerico(a);
  }

  get digitoVerificacion(): string {
    const num = this.form?.get('numero')?.value ?? '';
    if (!this.esNIT || !/^[0-9]{9}$/.test(String(num))) {
      return '';
    }
    return calcularDigitoVerificacionNIT(String(num));
  }

  onNumeroInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const limpio = sanitizarNumeroDocumento(this.abreviaturaActual, input.value);
    input.value = limpio;
    this.form.get('numero')?.setValue(limpio, { emitEvent: true });
  }

  get maxLengthNumero(): number {
    return maxLengthNumeroDocumento(this.abreviaturaActual);
  }

  get placeholderNumero(): string {
    if (this.esNIT) {
      return 'Solo 9 dígitos (sin dígito de verificación)';
    }
    if (this.abreviaturaActual === 'PAS') {
      return 'Ej: AB1234567 (7 a 9 caracteres)';
    }
    return 'Número de identificación';
  }

  hasError(campo: string): boolean {
    const c = this.form.get(campo);
    return !!(c && c.errors && c.touched);
  }

  getErrorMessage(campo: string): string {
    const c = this.form.get(campo);
    if (!c || !c.errors || !c.touched) {
      return '';
    }
    if (c.errors['required']) {
      return 'Este campo es obligatorio';
    }
    if (c.errors['tipoInvalido']) {
      return 'Debe seleccionar un tipo de identificación';
    }
    if (c.errors['sincodigo']) {
      return mensajeNumeroDocumentoInvalido(this.abreviaturaActual);
    }
    if (c.errors['pattern']) {
      return 'Formato no válido.';
    }
    return '';
  }

  ngSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      return;
    }
    this.verificarIdentidad();
  }

  verificarIdentidad(): void {
    const tipo = parseInt(this.form.value.tipo, 10);
    let numeroIdentificacion: string = String(this.form.value.numero ?? '').trim();

    if (this.esNIT) {
      numeroIdentificacion = numeroIdentificacion + calcularDigitoVerificacionNIT(numeroIdentificacion);
    }

    const request = {
      tipoIdentificacionId: tipo,
      numeroIdentificacion
    };
    this.uiHelper.showSpinner('Verificando identidad...', 'admin');
    this.resetPassword
      .verificarIdentidad(request)
      .pipe(finalize(() => this.uiHelper.hideSpinner()))
      .subscribe({
        next: (res) => {
          if (res?.data) {
            this.resetPassword.setStorage('datosVerificacion', JSON.stringify(res.data));
          }
          this.next.emit();
        },
        error: (err) => {
          const msg =
            (err && (err as { message?: string }).message) ||
            err?.error?.mensaje ||
            err?.error?.descripcionErrores?.join('\n') ||
            err?.error?.message ||
            err?.message ||
            'No fue posible verificar la identidad.';
          Swal.fire({
            title: 'Error',
            text: msg,
            icon: 'error',
            ...this.notification.getSwalBaseConfigReloadAfterClose()
          });
        }
      });
  }

  private abreviaturaDeId(tipoId: unknown): string {
    return abreviaturaPorIdTipo(tipoId, this.tiposDocumentoService.snapshot);
  }
}
